import {
  compact,
  findIndex,
  flatten,
  forEach,
  groupBy,
  has,
  reject,
  some,
  sortBy,
  startCase,
  update,
} from 'lodash';

import {
  FacetLabelKey,
  OperatorTruckIndexRecord,
  SearchIndexResult,
  TransformHitRecordFn,
} from 'lib/search';

/**
 * Format the facet values to display them properly
 * @param facetAttribute
 * @param facetValue
 * @returns
 */
export function getFacetValue(
  facetAttribute: FacetLabelKey,
  facetValue: string,
  facetValueFormatter?: (
    facetAttribute: FacetLabelKey,
    facetValue: string
  ) => string
) {
  if (
    facetAttribute === 'operator.tags' ||
    facetAttribute === 'truck.tags' ||
    facetAttribute === 'company.tags'
  ) {
    return facetValue;
  }
  // If no formatter function is provided then
  // format facet value to uppercase by default.
  return !!facetValueFormatter
    ? facetValueFormatter(facetAttribute, facetValue)
    : startCase(facetValue);
}

/**
 * Generate a label for a given facet label key
 * @param facetKey facet label key
 * @returns string
 */
export function getDefaultFacetLabel(facetKey: FacetLabelKey): string {
  return startCase(facetKey);
}

/**
 * Replaces employee operator truck records with owner operator truck records within the search results.
 * If an owner exists within the company's results, employee records are discarded. If no owner is found,
 * the first employee's record is replaced with the owner's record.
 *
 * @async
 * @function replaceEmployeesWithOwners
 * @param {OperatorTruckIndexRecord[]} results - An array of operator truck index records.
 * @param {TransformHitRecordFn} replaceEmployeeWithOwner - A function that transforms an employee record to an owner record.
 * @param {boolean} isExplicitlyExcludingEmployees - flag to inform if the operator employees should be effectively replaced or kept.
 * @returns {Promise<OperatorTruckIndexRecord[]>} An array of operator truck index records with employees replaced by owners where applicable.
 */
export async function replaceEmployeesWithOwners(
  results: OperatorTruckIndexRecord[],
  replaceEmployeeWithOwner: TransformHitRecordFn<OperatorTruckIndexRecord>,
  isExplicitlyExcludingEmployees?: boolean
): Promise<OperatorTruckIndexRecord[]> {
  const resultsGroupedByCompany = groupBy(results, 'company.companyId');
  const employeeRoleStr = 'operatorEmployee';
  const ownerRoleStr = 'operatorOwner';
  // Check if should keep employees options in the result set or not
  const keepEmployees = !isExplicitlyExcludingEmployees;

  const processedResults: OperatorTruckIndexRecord[][] = [];
  await new Promise((resolve) => {
    const queue = Queue();
    forEach(resultsGroupedByCompany, (companyResults_) => {
      queue.push(async () => {
        async function loadCompanyResults() {
          // Add number of matching results as metadata to each result
          const _matchingResults = companyResults_.length;
          const companyResults = companyResults_.map((result) => ({
            ...result,
            _matchingResults,
          }));
          // Check if there's some employee from this company in the results
          if (some(companyResults, ['operator.role', employeeRoleStr])) {
            // If there's some owner then just discard employees results (if should not keep employees)
            if (some(companyResults, ['operator.role', ownerRoleStr])) {
              return keepEmployees
                ? companyResults
                : reject(companyResults, ['operator.role', employeeRoleStr]);
            } else {
              // replace all the company results with a single owner (if should not keep employees)
              const operatorEmployee = companyResults[0];
              const owner = await replaceEmployeeWithOwner(operatorEmployee);
              return compact(
                keepEmployees ? [owner, ...companyResults] : [owner]
              );
            }
          } else {
            return companyResults;
          }
        }
        const results = await loadCompanyResults();
        processedResults.push(results as OperatorTruckIndexRecord[]);
        return;
      });
    });
    queue.push(async () => resolve(null));
  });

  // Sort processed results with original order
  const flattenedResults = flatten(processedResults);
  const sortedResults = sortBy(flattenedResults, (result) => {
    const originalIndex = findIndex(results, ['objectID', result.objectID]);
    if (originalIndex >= 0) {
      return originalIndex;
    }
    return findIndex(results, ['company.companyId', result.company.companyId]);
  });
  return sortedResults;
}

/**
 * Parses timestamp fields within a search result hit object, converting them from numeric timestamps to Date objects.
 * @param hit The search result hit object to parse.
 * @param timestampsFields An array of field paths within the hit object that contain timestamp values.
 * @returns A new object with the specified timestamp fields parsed as Date objects.
 * @template T Extends SearchIndexResult
 */
export function parseTimestampsFields<T extends SearchIndexResult>(
  hit: T,
  timestampsFields: string[]
) {
  // Only update fields that have some valid value
  const fieldsToUpdate = timestampsFields.filter((field) => has(hit, field));

  let parsedHit = hit;
  fieldsToUpdate.forEach((field) => {
    parsedHit = update(
      parsedHit,
      field,
      (timestamp: number) => new Date(timestamp)
    );
  });

  return parsedHit;
}

/**
 * Creates a queue for managing and executing asynchronous tasks with controlled concurrency.
 *
 * This function initializes a queue that allows a specified number of asynchronous tasks to run concurrently.
 * Additional tasks are queued until an active task completes. This is useful for rate limiting or managing resource usage
 * when performing a large number of asynchronous operations, such as API calls or database transactions.
 *
 * @param {number} [concurrency=1] - The maximum number of asynchronous tasks that can run concurrently.
 * @returns An object with a `push` method for adding tasks to the queue.
 */
export function Queue(concurrency = 1) {
  type AsyncFn = () => Promise<unknown>;
  let running = 0;
  const taskQueue: AsyncFn[] = [];

  const runTask = (asyncTask: AsyncFn) => {
    running++;
    asyncTask().finally(() => {
      running--;
      if (!!taskQueue.length) {
        const nextTask = taskQueue.shift() as AsyncFn;
        runTask(nextTask);
      }
    });
  };

  const enqueueTask = (asyncTask: AsyncFn) => taskQueue.push(asyncTask);

  return {
    push: (asyncTask: AsyncFn) =>
      running < concurrency ? runTask(asyncTask) : enqueueTask(asyncTask),
  };
}

/**
 * Generates a filter string for a numerical range.
 * It allows consumers to filter by a numerical range without
 * knowing the syntax required by Algolia.
 *
 * Example:
 * For a field named 'age', a lowest value of 20, and a highest value of 30,
 * the generated filter string will be 'age:20 TO 30'.
 *
 * @param {string} field - The name of the field to filter on.
 * @param {number} lowestValue - The lowest value of the range.
 * @param {number} highestValue - The highest value of the range.
 * @returns {string} A string representing the range filter.
 */
export function generateRangeFilterStr(
  field: string,
  lowestValue: number,
  highestValue: number
): string {
  return `${field}:${lowestValue} TO ${highestValue}`;
}
